// ProfileSidebar.jsx - Profile as a right-aligned sidebar
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { authService } from "../../services/auth";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import ConfirmModal from "./ConfirmModal";
import { User, Settings, AlertTriangle, Bell } from 'lucide-react';
import { MOBILE_PHONE_ICON } from "../../constants/icons";
import { isAndroid, isIOS, isInstalled, openGooglePlay } from "../../utils/pwaInstall";
import { installPrompts } from "../../config/installPrompts";
import { useAnalytics } from "../../contexts/AnalyticsContext";
import "../../styles/profile-modal.css";
import logger from '../../utils/logger';
import { APP_VERSION } from '../../version';

function ProfileSidebar({ user, notifications = [], onUserUpdate, onClose, onLogout, removeNotification, clearNotifications, darkMode, setDarkMode, onQuickImport, focusUsername = false }) {
  const { trackClick } = useAnalytics();
  const navigate = useNavigate();
  const usernameInputRef = useRef(null);
  const [username, setUsername] = useState(user?.username || "");
  const [bggId, setBggId] = useState(user?.bgg_id || "");
  const [saving, setSaving] = useState(false);
  const [showUsernameInfo, setShowUsernameInfo] = useState(false);
  const [showBggInfo, setShowBggInfo] = useState(false);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const [banner, setBanner] = useState({ show: false, message: "", type: "info" });

  // BGG Sync state
  const [bggImportLoading, setBggImportLoading] = useState(false);
  const [showBggImportConfirm, setShowBggImportConfirm] = useState(false);
  const [hasExistingCollection, setHasExistingCollection] = useState(false);

  // Account deletion state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Password reset state
  const [resetLoading, setResetLoading] = useState(false);

  // Section expand state (notifications open by default; Account also opens
  // when the sidebar is asked to focus the username field)
  const [expanded, setExpanded] = useState({
    account: !!focusUsername,
    settings: false,
    notifications: true,
    danger: false,
  });
  const toggleSection = (section) =>
    setExpanded((prev) => ({ ...prev, [section]: !prev[section] }));

  // Focus the username input on mount when requested (e.g. from the
  // username reminder banner's "Update Username" CTA)
  useEffect(() => {
    if (focusUsername && usernameInputRef.current) {
      usernameInputRef.current.focus();
      usernameInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate unread notification count
  const unreadCount = notifications.filter(n => !n.read).length;
  const displayedNotifications = showAllNotifications ? notifications : notifications.slice(0, 5);

  // Update user data when prop changes
  useEffect(() => {
    setUsername(user?.username || "");
    setBggId(user?.bgg_id || "");
  }, [user]);

  // Function to show banner notification
  const showBanner = (message, type = 'info') => {
    setBanner({
      show: true,
      message,
      type
    });

    // Auto-hide after 3 seconds
    setTimeout(() => {
      setBanner(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  // Check if user has existing collection
  useEffect(() => {
    const checkExistingCollection = async () => {
      if (!user?.id) return;

      try {
        const res = await httpRequest(`${API_BASE}/profile/collection`, {
          method: "GET",
          headers: authService.getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setHasExistingCollection(data.length > 0);
        }
      } catch (err) {
        logger.error("Failed to check collection:", err);
        setHasExistingCollection(false);
      }
    };

    checkExistingCollection();
  }, [user]);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Save user data to backend
  const saveUsername = async (newUsername) => {
    if (!user || !newUsername.trim()) return;
    setSaving(true);
    try {
      await authService.updateUsername(newUsername.trim());
      onUserUpdate();
    } catch (err) {
      logger.error("Failed to update username:", err);
      showBanner(err.message || "Failed to save username", "error");
    } finally {
      setSaving(false);
    }
  };

  const saveBggId = async (newBggId) => {
    if (!user) return;
    setSaving(true);
    try {
      await authService.updateBggId(newBggId?.trim() || null);
      onUserUpdate();
    } catch (err) {
      logger.error("Failed to update BGG ID:", err);
      showBanner(err.message || "Failed to save BGG ID", "error");
    } finally {
      setSaving(false);
    }
  };

  // Account deletion handler
  const handleDeleteAccount = async () => {
    if (!user) return;
    setDeleteLoading(true);
    try {
      await authService.deleteAccount();
      trackClick('account_deletion_requested');
      setShowDeleteConfirm(false);
      await onLogout();
      navigate("/");
      onClose();
    } catch (err) {
      logger.error("Failed to request account deletion:", err);
      showBanner(err.message || "Failed to request account deletion", "error");
      setDeleteLoading(false);
    }
  };

  // BGG Sync handler
  const handleImportBGG = async () => {
    if (!user?.bgg_id?.trim()) {
      showBanner("Please set your BGG username/ID first", "warning");
      return;
    }

    // Check if user has existing collection and show confirmation if needed
    if (hasExistingCollection) {
      setShowBggImportConfirm(true);
      return;
    }

    // Direct import for first-time users
    performBggImport();
  };

  const performBggImport = async () => {
    trackClick('bgg_import_start', { bgg_id: user?.bgg_id });
    setBggImportLoading(true);
    try {
      // Import all games from BGG collection (including owned, previously owned, wishlist, etc.)
      const res = await httpRequest(`${API_BASE}/profile/collection/import-bgg?include_all=true`, {
        method: "POST",
        headers: authService.getAuthHeaders(),
      });

      if (res.ok) {
        const data = await res.json();
        if (data && data.imported > 0) {
          trackClick('bgg_import_success', { bgg_id: user?.bgg_id, imported_count: data.imported });
          showBanner(`Imported ${data.imported} games from BGG`, 'success');
          if (onUserUpdate) onUserUpdate();
        } else {
          trackClick('bgg_import_success', { bgg_id: user?.bgg_id, imported_count: 0 });
          showBanner("No new games to import", "info");
        }
      } else {
        const error = await res.json();
        showBanner(error.detail || "Failed to import collection", "error");
      }
    } catch (err) {
      logger.error("BGG import error:", err);
      trackClick('bgg_import_failure', { bgg_id: user?.bgg_id, error: err?.message });
      showBanner("Failed to import collection", "error");
    } finally {
      setBggImportLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      window.location.href = '/';
    } catch (error) {
      logger.error('Logout failed:', error);
    }
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const handleQuickImport = async () => {
    await onQuickImport?.();
    onClose();
  };


  return (
    <div className="profile-sidebar-overlay" onClick={onClose}>
      <div className="profile-sidebar" onClick={(e) => e.stopPropagation()}>
        {/* Banner Notification */}
        {banner.show && (
          <div className={`banner-notification banner-${banner.type}`}>
            {banner.message}
          </div>
        )}
        <div className="profile-sidebar-header">
          <h2>Profile</h2>
          <button className="profile-sidebar-close" onClick={onClose} aria-label="Close profile">
            ×
          </button>
        </div>

        <div className="profile-sidebar-content">
          {/* Account Section */}
          <div className="profile-section">
            <div
              className="profile-section-header"
              onClick={() => toggleSection('account')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleSection('account');
                }
              }}
              role="button"
              aria-expanded={expanded.account}
              tabIndex={0}
              style={{ cursor: 'pointer' }}
            >
              <h3 className="section-title" style={{ margin: 0 }}>
                <User size={18} aria-hidden="true" />Account
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {installPrompts.showProfileGooglePlay && installPrompts.googlePlayUrl && !Capacitor.isNativePlatform() && isAndroid() && (
                  <button
                    onClick={(e) => { e.stopPropagation(); openGooglePlay(); }}
                    className="go-to-app-button-sidebar"
                    aria-label="Get the mobile app on Google Play"
                    title="Get the mobile app on Google Play"
                    style={{
                      color: 'var(--pista-button-bg, #2E7D32)',
                      width: '2.25rem',
                      height: '2.25rem',
                      borderRadius: '25%',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'transparent',
                      border: '2px solid var(--pista-button-bg, #2E7D32)',
                      cursor: 'pointer',
                      fontSize: '1.1rem',
                      padding: 0,
                      flexShrink: 0
                    }}
                  >
                    {MOBILE_PHONE_ICON}
                  </button>
                )}
                <span aria-hidden="true" style={{ fontSize: '0.75rem' }}>{expanded.account ? '▼' : '▶'}</span>
              </div>
            </div>

            <div
              className="section-content"
              style={{
                maxHeight: expanded.account ? '1500px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.3s ease'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                <button
                  onClick={handleLogout}
                  className="profile-logout-button"
                  style={{ width: 'auto', padding: '0.5rem 0.75rem' }}
                >
                  Logout
                </button>
              </div>
              <div className="profile-field">
              <label htmlFor="username-input">Username</label>
              <div className="input-with-info">
                <input
                  id="username-input"
                  ref={usernameInputRef}
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onBlur={() => saveUsername(username)}
                  className="profile-input"
                  disabled={saving}
                />
                <button
                  type="button"
                  className="info-icon"
                  onClick={() => setShowUsernameInfo(!showUsernameInfo)}
                  aria-label="Username info"
                  title="Click for more information"
                >
                  ℹ️
                </button>
              </div>
              {showUsernameInfo && (
                <div className="info-tooltip">
                  Your display name across the platform. This is how other users will see you identified in the application.
                </div>
              )}
            </div>

            {user?.persona_title ? (
              <div style={{ marginBottom: "1rem" }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  backgroundColor: "var(--bg-secondary, #f5f5f5)",
                  borderRadius: "8px",
                  marginBottom: "0.75rem",
                  border: "1px solid var(--border-color, #e0e0e0)"
                }}>
                  {user.persona_icon && (
                    <span style={{ fontSize: "2rem", lineHeight: 1 }}>{user.persona_icon}</span>
                  )}
                  <span style={{ fontWeight: 600, color: "var(--text-primary, #333)" }}>
                    {user.persona_title}
                  </span>
                </div>
                <a
                  href="/survey"
                  className="profile-button"
                  style={{ display: "block", textAlign: "center", textDecoration: "none" }}
                >
                  Refresh persona
                </a>
              </div>
            ) : (
              <a
                href="/survey"
                className="profile-button profile-button-primary"
                style={{ display: "block", textAlign: "center", textDecoration: "none", marginBottom: "1rem" }}
              >
                Discover your gaming superpower
              </a>
            )}

            <div className="profile-field">
              <label htmlFor="bgg-input">BGG Username/ID</label>
              <div className="input-with-info">
                <input
                  id="bgg-input"
                  type="text"
                  value={bggId}
                  onChange={(e) => setBggId(e.target.value)}
                  onBlur={() => saveBggId(bggId)}
                  className="profile-input"
                  placeholder="Enter BGG username or ID"
                  disabled={saving}
                />
                <button
                  type="button"
                  className="info-icon"
                  onClick={() => setShowBggInfo(!showBggInfo)}
                  aria-label="BGG info"
                  title="Click for more information"
                >
                  ℹ️
                </button>
              </div>
              {showBggInfo && (
                <div className="info-tooltip">
                  Used to import your BoardGameGeek collection. Enter your BGG username or ID to sync your board game collection from BoardGameGeek.
                </div>
              )}
            </div>

            {/* Quick Import Button */}
            <button
              onClick={handleQuickImport}
              className="profile-button profile-button-secondary"
              style={{
                marginTop: "0.5rem",
                width: "100%",
                fontSize: "0.875rem",
                padding: "0.625rem 1rem"
              }}
            >
              Quick Import
            </button>

            {/* BGG Sync Button - Only show for first-time import */}
            {user?.bgg_id?.trim() && !hasExistingCollection && (
              <button
                onClick={handleImportBGG}
                data-track-handled="true"
                disabled={bggImportLoading}
                className="profile-button profile-button-primary"
                style={{
                  marginTop: "0.5rem",
                  width: "100%",
                  opacity: bggImportLoading ? 0.7 : 1,
                  cursor: bggImportLoading ? "not-allowed" : "pointer"
                }}
              >
                {bggImportLoading ? "Importing..." : "Import history"}
              </button>
            )}

            {/* Password Reset */}
            <div
              style={{
                marginTop: "1.5rem",
                paddingTop: "1rem",
                borderTop: "1px solid var(--border-color, #eee)",
              }}
            >
              <button
                onClick={async () => {
                  if (!user?.email) {
                    showBanner('No email address found for your account', 'error');
                    return;
                  }
                  setResetLoading(true);
                  try {
                    await authService.requestPasswordReset(user.email);
                    showBanner('Password reset link sent to your email', 'success');
                  } catch (err) {
                    showBanner(err.message || 'Failed to send reset email', 'error');
                  } finally {
                    setResetLoading(false);
                  }
                }}
                disabled={resetLoading}
                className="profile-button"
                style={{
                  width: "100%",
                  cursor: resetLoading ? "not-allowed" : "pointer",
                  opacity: resetLoading ? 0.7 : 1
                }}
              >
                {resetLoading ? "Sending..." : "Reset Password"}
              </button>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #666)', marginTop: '0.5rem' }}>
                We&apos;ll send a password reset link to your email address.
              </div>
            </div>
            </div>
          </div>

          {/* Settings Section */}
          <div className="profile-section">
            <div
              className="profile-section-header"
              onClick={() => toggleSection('settings')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleSection('settings');
                }
              }}
              role="button"
              aria-expanded={expanded.settings}
              tabIndex={0}
              style={{ cursor: 'pointer' }}
            >
              <h3 className="section-title" style={{ margin: 0 }}>
                <Settings size={18} aria-hidden="true" />Settings
              </h3>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span aria-hidden="true" style={{ fontSize: '0.75rem' }}>{expanded.settings ? '▼' : '▶'}</span>
              </div>
            </div>

            <div
              className="section-content"
              style={{
                maxHeight: expanded.settings ? '1000px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.3s ease'
              }}
            >
              <div className="setting-item">
              <div className="setting-info">
                <span className="setting-label">🌙 Dark Mode</span>
                <span className="setting-description">Toggle dark theme</span>
              </div>
              <button
                className={`setting-toggle ${darkMode ? 'active' : ''}`}
                onClick={toggleDarkMode}
                aria-label="Toggle dark mode"
              >
                <span className="toggle-slider"></span>
              </button>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-label">App Version</span>
                <span className="setting-description">Current release version</span>
              </div>
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary, #333)' }}>
                v{APP_VERSION}
              </span>
            </div>

            {installPrompts.showSettingsIOS && isIOS() && !isInstalled() && !Capacitor.isNativePlatform() && (
              <div className="setting-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
                <div className="setting-info">
                  <span className="setting-label">Add to Home Screen</span>
                  <span className="setting-description">Install Pista like an app on your iPhone/iPad</span>
                </div>
                <ol style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #666)', paddingLeft: '1.25rem', margin: 0 }}>
                  <li>Tap the <strong>Share</strong> button in Safari</li>
                  <li>Scroll and tap <strong>Add to Home Screen</strong></li>
                </ol>
              </div>
            )}
            </div>
          </div>

          {/* Danger Section */}
          <div className="profile-section danger-section">
            <div
              className="profile-section-header"
              onClick={() => toggleSection('danger')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleSection('danger');
                }
              }}
              role="button"
              aria-expanded={expanded.danger}
              tabIndex={0}
              style={{ cursor: 'pointer' }}
            >
              <h3 className="section-title danger-title" style={{ margin: 0 }}>
                <AlertTriangle size={18} aria-hidden="true" />Danger Zone
              </h3>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span aria-hidden="true" style={{ fontSize: '0.75rem' }}>{expanded.danger ? '▼' : '▶'}</span>
              </div>
            </div>

            <div
              className="section-content"
              style={{
                maxHeight: expanded.danger ? '1000px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.3s ease'
              }}
            >
              <div className="danger-description" style={{
                fontSize: "0.8rem",
                color: "var(--text-secondary, #666)",
                marginBottom: "1rem",
                padding: "0.5rem",
                backgroundColor: "var(--bg-secondary, #fafafa)",
                border: "1px solid var(--border-color, #eee)",
                borderRadius: "4px"
              }}>
                These actions can modify or permanently erase your data.
              </div>

              {user?.bgg_id?.trim() && hasExistingCollection && (
                <button
                  onClick={handleImportBGG}
                  data-track-handled="true"
                  disabled={bggImportLoading}
                  className="profile-button profile-button-danger"
                  style={{
                    width: "100%",
                    marginBottom: "1rem",
                    opacity: bggImportLoading ? 0.7 : 1,
                    cursor: bggImportLoading ? "not-allowed" : "pointer",
                    backgroundColor: "var(--danger-color, #d32f2f)",
                    borderColor: "var(--danger-color, #d32f2f)",
                    color: "white"
                  }}
                >
                  {bggImportLoading ? "Importing..." : "Re-import history"}
                </button>
              )}

              <div className="danger-description" style={{
                fontSize: "0.8rem",
                color: "var(--text-secondary, #666)",
                marginBottom: "1rem",
                padding: "0.5rem",
                backgroundColor: "var(--bg-secondary, #fafafa)",
                border: "1px solid var(--border-color, #eee)",
                borderRadius: "4px"
              }}>
                Deleting your account starts a 30-day grace period. You can reactivate at any time until then. After 30 days, your data is permanently erased.
              </div>

              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="profile-button profile-button-danger"
                style={{
                  width: "100%",
                  cursor: "pointer",
                  backgroundColor: "var(--danger-color, #d32f2f)",
                  borderColor: "var(--danger-color, #d32f2f)",
                  color: "white"
                }}
              >
                Delete Account
              </button>
            </div>
          </div>

          {/* Notifications Section */}
          <div className="notifications-section">
            <div
              className="notifications-header"
              onClick={() => toggleSection('notifications')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleSection('notifications');
                }
              }}
              role="button"
              aria-expanded={expanded.notifications}
              tabIndex={0}
              style={{ cursor: 'pointer' }}
            >
              <h3 className="section-title" style={{ margin: 0 }}>
                <Bell size={18} aria-hidden="true" />Notifications
                {unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount}</span>
                )}
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {notifications.length > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); clearNotifications(); }}
                    className="clear-notifications-button"
                  >
                    Mark all as read
                  </button>
                )}
                <span aria-hidden="true" style={{ fontSize: '0.75rem' }}>{expanded.notifications ? '▼' : '▶'}</span>
              </div>
            </div>

            <div
              className="section-content"
              style={{
                maxHeight: expanded.notifications ? '1000px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.3s ease'
              }}
            >
              <div className="notifications-list">
              {displayedNotifications.length > 0 ? (
                <>
                  {displayedNotifications.map((notification, index) => (
                    <div
                      key={notification.id || index}
                      className={`notification-item ${!notification.read ? 'unread' : ''}`}
                    >
                      <div className="notification-content">
                        <div className="notification-message">{notification.message}</div>
                        <div className="notification-time">
                          {notification.created_at ? new Date(notification.created_at).toLocaleString() : ''}
                        </div>
                      </div>
                      <button
                        onClick={() => removeNotification(notification.id)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--text-tertiary, #999)",
                          fontSize: "0.8rem",
                          cursor: "pointer",
                          padding: "0.25rem",
                          borderRadius: "4px"
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = "var(--bg-secondary, #f0f0f0)"}
                        onMouseOut={(e) => e.target.style.backgroundColor = "transparent"}
                        title="Mark as read"
                      >
                        ✓
                      </button>
                    </div>
                  ))}

                  {notifications.length > 5 && (
                    <button
                      className="show-more-button"
                      onClick={() => setShowAllNotifications(!showAllNotifications)}
                    >
                      {showAllNotifications ? 'Show Less' : `Show More (${notifications.length - 5} more)`}
                    </button>
                  )}
                </>
              ) : (
                <div className="no-notifications">
                  No notifications
                </div>
              )}
            </div>
            </div>
          </div>
        </div>

        {/* Delete Account Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="profile-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="profile-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="delete-account-title">
              <div className="profile-modal-header">
                <h2>Delete Account</h2>
                <button
                  className="profile-modal-close"
                  onClick={() => setShowDeleteConfirm(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="profile-modal-content">
                <p style={{ marginBottom: "1rem" }}>
                  This will lock your account and start a <strong>30-day grace period</strong>. After that, your account and data will be permanently deleted.
                </p>
                <p style={{ marginBottom: "1rem" }}>
                  Please confirm that you want to delete your account. This action cannot be undone.
                </p>
              </div>
              <div className="profile-modal-footer" style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
                <button
                  className="profile-button profile-logout-button"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  className="profile-button profile-button-danger"
                  onClick={handleDeleteAccount}
                  data-track-handled="true"
                  disabled={deleteLoading}
                  style={{
                    backgroundColor: "var(--danger-color, #d32f2f)",
                    borderColor: "var(--danger-color, #d32f2f)",
                    color: "white",
                    opacity: deleteLoading ? 0.6 : 1
                  }}
                >
                  {deleteLoading ? "Deleting..." : "Delete Account"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* BGG Import Confirmation Modal */}
        <ConfirmModal
          isOpen={showBggImportConfirm}
          title="Re-import Collection from BGG"
          message="You already have games in your collection. Re-importing from BGG will update existing games and add new ones. This action cannot be undone. Do you want to continue?"
          confirmText="Re-import Collection"
          cancelText="Cancel"
          type="danger"
          onConfirm={() => {
            setShowBggImportConfirm(false);
            performBggImport();
          }}
          onCancel={() => setShowBggImportConfirm(false)}
        />

      </div>
    </div>
  );
}

export default ProfileSidebar;
